import { useMutation, useQuery, useQueryClient } from 'react-query';
import { predictionManagerAxiosApi } from 'src/Config/axiosApi';
import { VENUE_ACCOUNTS_KEY, VenueAccount, VenuePlatform } from './useVenueAccounts';

/**
 * The accounts screen's API surface: per-consumer credential health
 * (`GET /accounts/health`) and the registry edits (`PATCH
 * /accounts/:platform/:accountId`). The registry READ itself stays in
 * `useVenueAccounts`.
 *
 * Nothing here ever carries credential material — the api keys live only in
 * the k8s Secret an account names, and the patch body is built from a
 * whitelist so a field added to the form cannot smuggle any in.
 */

export interface AccountConsumerHealth {
  consumer: string;
  reachable: boolean;
  reported: boolean;
  stale: boolean;
  credentialsMounted: boolean | null;
  missingKeys: string[];
  checkedAt: string | null;
  ageMs: number | null;
}

export interface AccountHealthEntry {
  accountId: string;
  platform: VenuePlatform;
  name: string;
  enabled: boolean;
  reachable: boolean;
  consumers: AccountConsumerHealth[];
}

export interface AccountsHealthResponse {
  staleAfterMs: number;
  readAt: string;
  /** The consumers expected to report. A roster entry with no report is ABSENT. */
  consumers: string[];
  accounts: AccountHealthEntry[];
}

export const ACCOUNTS_HEALTH_KEY = ['venue-accounts-health'];

export async function fetchAccountsHealth(): Promise<AccountsHealthResponse> {
  const { data } =
    await predictionManagerAxiosApi.get<AccountsHealthResponse>(
      '/accounts/health'
    );
  if (!data || !Array.isArray(data.accounts) || !Array.isArray(data.consumers)) {
    throw new Error('Malformed accounts-health response');
  }
  return data;
}

export function useAccountsHealth(enabled = true) {
  return useQuery<AccountsHealthResponse>(
    ACCOUNTS_HEALTH_KEY,
    fetchAccountsHealth,
    { enabled, refetchInterval: 30_000, refetchOnWindowFocus: false }
  );
}

/**
 * How one consumer's credentials read RIGHT NOW.
 *
 * Only `ok` is green, and it is reachable ONLY for a fresh report. The other
 * four are all "not reachable" but stay apart, because each points somewhere
 * else: nothing ever arrived (`absent` — the pod never called), the pod
 * answered but has not checked yet (`never-reported`), the last check is older
 * than `staleAfterMs` (`stale` — the pod may be gone), or the check itself said
 * no (`unreachable` — keys missing or rejected).
 */
export type ConsumerHealthState =
  | 'ok'
  | 'absent'
  | 'never-reported'
  | 'stale'
  | 'unreachable';

export function classifyConsumer(
  entry: AccountConsumerHealth | undefined
): ConsumerHealthState {
  if (!entry) return 'absent';
  if (!entry.reported) return 'never-reported';
  if (entry.stale) return 'stale';
  return entry.reachable ? 'ok' : 'unreachable';
}

export const CONSUMER_STATE_LABEL: Record<ConsumerHealthState, string> = {
  ok: 'ok',
  absent: 'no report',
  'never-reported': 'never checked',
  stale: 'stale',
  unreachable: 'unreachable'
};

export function isConsumerReachable(state: ConsumerHealthState): boolean {
  return state === 'ok';
}

export interface ResolvedConsumer {
  consumer: string;
  state: ConsumerHealthState;
  entry?: AccountConsumerHealth;
}

/**
 * Join the expected roster against what the account reported, so a consumer
 * that stopped reporting keeps a row instead of silently vanishing from the
 * screen.
 */
export function resolveConsumers(
  roster: string[],
  account: AccountHealthEntry | undefined
): ResolvedConsumer[] {
  const reported = account?.consumers ?? [];
  const names = [...roster];
  for (const entry of reported) {
    if (!names.includes(entry.consumer)) names.push(entry.consumer);
  }
  return names.map(consumer => {
    const entry = reported.find(c => c.consumer === consumer);
    return { consumer, state: classifyConsumer(entry), entry };
  });
}

export function findAccountHealth(
  health: AccountsHealthResponse | undefined,
  platform: VenuePlatform,
  accountId: string
): AccountHealthEntry | undefined {
  return health?.accounts.find(
    a => a.platform === platform && a.accountId === accountId
  );
}

/**
 * Whether an account HAS a credential identity configured — never what it is.
 */
export function hasIdentity(account: Pick<VenueAccount, 'secretName'>): boolean {
  return Boolean(account.secretName);
}

export const MASKED_IDENTITY = '••••••••';

/**
 * Per-account rate-limit pressure, `GET /accounts/rate-limits`.
 *
 * The two counters are reported and rendered SEPARATELY and must never be
 * summed: `venueRejected429` is the venue refusing us and only another ACCOUNT
 * relieves it, while `selfThrottled` is our own token bucket waiting and is
 * tuned in config. Adding them lets self-throttling masquerade as venue
 * pressure and sends the operator after the wrong fix.
 *
 * A `null` count means the window is not covered — NOT zero. The backend types
 * it that way on purpose, and a `?? 0` here would recreate the false all-clear
 * the whole counter exists to kill.
 */
export type VenueAccountCoverageReason =
  | 'stamped'
  | 'no_stamped_rows'
  | 'window_predates_stamping'
  | 'unattributed_rows_in_window';

export interface VenueAccountRateLimitCoverage {
  /** Earliest account-stamped row; `null` = nothing is stamped at all. */
  since: string | null;
  /** `false` means the counts are UNKNOWN, never that they are zero. */
  covered: boolean;
  reason: VenueAccountCoverageReason;
}

export interface VenueAccountRateLimitRow {
  accountId: string;
  platform: VenuePlatform;
  name: string;
  enabled: boolean;
  /** Venue 429s on the ORDER path. Only another account relieves this. */
  venueRejected429: number | null;
  /** OUR token bucket waiting. Tunable in config; never the venue refusing us. */
  selfThrottled: number | null;
  coverage: VenueAccountRateLimitCoverage;
}

export interface VenueAccountRateLimitsResponse {
  readAt: string;
  window: { from: string; to: string };
  windowHours: number;
  requestedWindowHours: number;
  clampedToRetention: boolean;
  retentionHours: number;
  maxWindowHours: number;
  /** Counted events in the window carrying NO account id, per platform. Any
   *  non-zero value makes every per-account number below a FLOOR, not a total. */
  unattributedInWindow: { kalshi: number; polymarket: number };
  coverage: VenueAccountRateLimitCoverage;
  accounts: VenueAccountRateLimitRow[];
}

export const ACCOUNTS_RATE_LIMITS_KEY = ['venue-accounts-rate-limits'];

export async function fetchAccountRateLimits(
  windowHours = 24
): Promise<VenueAccountRateLimitsResponse> {
  const { data } =
    await predictionManagerAxiosApi.get<VenueAccountRateLimitsResponse>(
      '/accounts/rate-limits',
      { params: { windowHours } }
    );
  if (!data || !Array.isArray(data.accounts)) {
    throw new Error('Malformed accounts rate-limits response');
  }
  return data;
}

export function useAccountRateLimits(windowHours = 24) {
  return useQuery<VenueAccountRateLimitsResponse>(
    [...ACCOUNTS_RATE_LIMITS_KEY, windowHours],
    () => fetchAccountRateLimits(windowHours),
    { refetchInterval: 60_000, refetchOnWindowFocus: false }
  );
}

/**
 * Scoped by platform because `accountId` is unique PER PLATFORM — an unscoped
 * match would print the other venue's 429s against this account.
 */
export function findAccountRateLimits(
  data: VenueAccountRateLimitsResponse | undefined,
  platform: VenuePlatform,
  accountId: string
): VenueAccountRateLimitRow | undefined {
  return data?.accounts?.find(
    a => a.platform === platform && a.accountId === accountId
  );
}

export const COVERAGE_REASON_LABEL: Record<VenueAccountCoverageReason, string> = {
  stamped: 'la ventana está cubierta: el número es real',
  no_stamped_rows:
    'ninguna notificación lleva todavía una cuenta — no hay nada que contar, que NO es lo mismo que cero',
  window_predates_stamping:
    'la ventana empieza antes de la primera fila estampada — acortala para obtener una respuesta cubierta',
  unattributed_rows_in_window:
    'hay eventos contados SIN cuenta dentro de la ventana: un cero acá no se distingue de un productor que no está estampando'
};

/**
 * How many counted events in the window belong to nobody, or `null` when the
 * payload does not report it — a backend that predates the field, or one whose
 * block arrived incomplete.
 *
 * NOT `0`: that is the field whose whole job is to say the per-account numbers
 * below are a FLOOR rather than a total, and a zero there is an all-clear
 * nobody measured. Summing the two platforms is fine — this is not the
 * venue-vs-self split — but only once both halves are known.
 */
export function unattributedTotal(
  data: VenueAccountRateLimitsResponse | undefined
): number | null {
  const u = data?.unattributedInWindow;
  if (u == null || typeof u !== 'object') return null;
  if (typeof u.kalshi !== 'number' || typeof u.polymarket !== 'number') {
    return null;
  }
  return u.kalshi + u.polymarket;
}

export interface VenueAccountPatch {
  name?: string;
  enabled?: boolean;
  maxQuotedMarkets?: number | null;
}

/**
 * The ONLY fields this screen may write. Built as a whitelist rather than by
 * stripping known-bad keys: an api key, secret, passphrase or wallet added to
 * the form later still cannot reach the wire.
 */
export function buildAccountPatch(input: Record<string, unknown>): VenueAccountPatch {
  const patch: VenueAccountPatch = {};
  if (typeof input.name === 'string') patch.name = input.name.trim();
  if (typeof input.enabled === 'boolean') patch.enabled = input.enabled;
  if ('maxQuotedMarkets' in input) {
    const value = input.maxQuotedMarkets;
    if (value === null) patch.maxQuotedMarkets = null;
    else if (typeof value === 'number') patch.maxQuotedMarkets = value;
  }
  return patch;
}

export type MaxQuotedMarketsParse =
  | { ok: true; value: number | null }
  | { ok: false; error: string };

/**
 * `null` (empty input) = uncapped. `0` is refused: it reads as "uncapped" to a
 * truthiness check and as "nothing may run" to a comparison, so the backend
 * rejects it and the form must not offer it either.
 */
export function parseMaxQuotedMarkets(raw: string): MaxQuotedMarketsParse {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: null };
  if (!/^\d+$/.test(trimmed)) return { ok: false, error: 'Whole number or empty' };
  const value = Number(trimmed);
  if (value < 1) return { ok: false, error: 'Must be at least 1 (empty = uncapped)' };
  return { ok: true, value };
}

export function useUpdateVenueAccount() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      platform,
      accountId,
      patch
    }: {
      platform: VenuePlatform;
      accountId: string;
      patch: Record<string, unknown>;
    }) => {
      const body = buildAccountPatch(patch);
      const { data } = await predictionManagerAxiosApi.patch<VenueAccount>(
        `/accounts/${platform}/${encodeURIComponent(accountId)}`,
        body
      );
      return data;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(VENUE_ACCOUNTS_KEY);
        qc.invalidateQueries(ACCOUNTS_HEALTH_KEY);
      }
    }
  );
}
